/**
 * SWISS School Portal — top navigation bar
 * ========================================
 * Present on every authenticated screen. Holds the primary sections,
 * the jump-to search, crisis support, and the account menu.
 *
 * Crisis support appears here exactly once on desktop. On mobile it
 * moves to a single sticky action at the foot of the page (see
 * Layout) — the redesign brief was explicit that the hotline must be
 * reachable in exactly one place per breakpoint, because the old
 * layout duplicated it in the sidebar, the header and several page
 * bodies at once.
 */

import React, { useState, useRef, useEffect } from 'react';
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, Search, Phone, Settings, LogOut, Building2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { CRISIS_HOTLINE, CRISIS_HOTLINE_TEL } from '../config/navigation';
import TierBadge from './TierBadge';
import './TopNav.css';

/** "Greenwood International School" → "GS" */
function initialsFor(name) {
  if (!name) return '—';
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '—';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

const TopNav = ({ sections, activeSectionKey, onMenuClick, onSearchClick }) => {
  const { school, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const schoolName = school?.shortName || school?.name || 'School';
  const isCrisisMode = location.pathname.startsWith('/crisis');

  // Dismiss the account menu on outside click or Escape.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  return (
    <header className={`topnav ${isCrisisMode ? 'crisis-mode' : ''}`}>
      <button
        type="button"
        className="topnav-burger"
        onClick={onMenuClick}
        aria-label="Open navigation menu"
      >
        <Menu size={20} aria-hidden="true" />
      </button>

      <Link to="/dashboard" className="topnav-brand">
        <img
          src={`${process.env.PUBLIC_URL}/SWISS_Logo_4.webp`}
          alt="SWISS"
          className="topnav-logo"
        />
        <span className="topnav-wordmark">SWISS</span>
      </Link>

      <nav className="topnav-sections" aria-label="Primary">
        {sections.map((section) => {
          const to = section.items ? section.items[0].path : section.to;
          const isActive = section.key === activeSectionKey;
          return (
            <NavLink
              key={section.key}
              to={to}
              className={[
                'topnav-section',
                isActive ? 'active' : '',
                section.tone === 'danger' ? 'danger' : '',
              ].filter(Boolean).join(' ')}
              aria-current={isActive ? 'page' : undefined}
            >
              {section.label}
            </NavLink>
          );
        })}
      </nav>

      <button
        type="button"
        className="topnav-search"
        onClick={onSearchClick}
        aria-label="Jump to a page"
      >
        <Search size={14} aria-hidden="true" />
        <span className="topnav-search-label">
          {isAdmin ? 'Search schools, admins…' : 'Jump to anything…'}
        </span>
        <kbd className="topnav-search-kbd" aria-hidden="true">/</kbd>
      </button>

      <a
        className="topnav-crisis"
        href={`tel:${CRISIS_HOTLINE_TEL}`}
        aria-label={`Crisis support, call ${CRISIS_HOTLINE}`}
      >
        <Phone size={13} aria-hidden="true" />
        <span>{CRISIS_HOTLINE}</span>
      </a>

      <div className="topnav-account" ref={menuRef}>
        <button
          type="button"
          className="topnav-avatar"
          onClick={() => setMenuOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label={`Account menu for ${schoolName}`}
          title={schoolName}
        >
          {initialsFor(schoolName)}
        </button>

        {menuOpen && (
          <div className="topnav-menu" role="menu">
            <div className="topnav-menu-head">
              <div className="topnav-menu-avatar" aria-hidden="true">
                <Building2 size={16} />
              </div>
              <div className="topnav-menu-id">
                <div className="topnav-menu-name">{schoolName}</div>
                <TierBadge tier={school?.tier} size="sm" />
              </div>
            </div>

            <button
              type="button"
              role="menuitem"
              className="topnav-menu-item"
              onClick={() => { setMenuOpen(false); navigate('/settings'); }}
            >
              <Settings size={15} aria-hidden="true" />
              Settings
            </button>

            <button
              type="button"
              role="menuitem"
              className="topnav-menu-item danger"
              onClick={logout}
            >
              <LogOut size={15} aria-hidden="true" />
              Sign out
            </button>
          </div>
        )}
      </div>
    </header>
  );
};

export default TopNav;
